import type { AxiosRequestConfig } from 'axios';

/**
 * Request config for a call that has to survive the sign-out navigation: the axios
 * fetch adapter with `keepalive`, so the browser finishes the request even after the
 * navigation `logtoSignOut` performs has unloaded the page. The default browser adapter
 * is XHR, which that navigation aborts. Same axios instance, so the auth and maintenance
 * interceptors still run.
 *
 * `keepalive` only protects the request once `fetch()` has been called, and axios runs
 * the async request interceptor (which awaits the bearer, and may wait on a Logto token
 * refresh behind a cross-tab lock) before dispatch. A navigation inside that phase still
 * loses the request.
 *
 * The fetch adapter adds `User-Agent: axios/<version>` and, unlike the XHR adapter,
 * keeps `Content-Type` on a body-less request. Chrome ignores a scripted User-Agent;
 * Firefox and Safari send it, so the request would reach the backend's request logger as
 * an unknown device. `false` removes a header in axios.
 *
 * Lives here rather than beside its caller so `keepaliveDelete.test.ts` can send the
 * real thing: it used to assert against a hand-copied literal, which meant a `signal`,
 * `timeout` or `validateStatus` added to either copy was exercised by neither test.
 */
export const OUTLIVE_THE_SIGN_OUT_NAVIGATION: AxiosRequestConfig = {
  adapter: 'fetch',
  fetchOptions: { keepalive: true },
  headers: { 'User-Agent': false, 'Content-Type': false },
};

/**
 * Request config for a JSON POST sent as the page may be going away (pagehide, a tab closing,
 * the app backgrounding): the same fetch adapter with `keepalive` as
 * `OUTLIVE_THE_SIGN_OUT_NAVIGATION`, but it does NOT strip `Content-Type`. That config sets it to
 * `false`, which AxiosHeaders keeps, so axios's JSON default cannot overwrite it and a JSON body
 * goes out as `text/plain`, which FastAPI does not parse (422). The `application/json` entry here
 * repeats the instance default (api-client.ts) on purpose, so this config never depends on it;
 * `keepaliveStatsPost.test.ts` asserts the header reaching fetch. Used by the Partners page stats
 * batches (TECH-86); never `navigator.sendBeacon`, which cannot carry the bearer token the
 * request interceptor attaches.
 *
 * `keepalive` covers a batch only once `fetch()` has been called (the caveat on
 * `OUTLIVE_THE_SIGN_OUT_NAVIGATION` above). A signed-in web viewer's bearer is read behind the
 * cross-tab Web Lock first, which resolves in a later task, so a batch sent on `pagehide` as a
 * tab closes, reloads or navigates away never reaches fetch and is lost (at most the last 10s of
 * counts). Signed-out viewers' batches go out at once, and a `visibilitychange` to hidden while
 * the page lives on (a tab or app switch) sends for everyone.
 *
 * keepalive bodies are capped at 64 KB in total per page, so callers keep their batches small
 * (the stats batches cap at 100 events, a few KB).
 */
export const OUTLIVE_THE_PAGE_JSON: AxiosRequestConfig = {
  adapter: 'fetch',
  fetchOptions: { keepalive: true },
  headers: { 'User-Agent': false, 'Content-Type': 'application/json' },
};
